import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import TransactionGraph from './components/TransactionGraph';
import TransactionTable from './components/TransactionTable';
import FilterPanel from './components/FilterPanel';
import PatternAnalysis from './components/PatternAnalysis';

const TransactionAnalysis = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedAddress, setSelectedAddress] = useState('');
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null);
  const [filters, setFilters] = useState({});
  const [isFilterCollapsed, setIsFilterCollapsed] = useState(false);
  const [activeView, setActiveView] = useState('split'); // 'split', 'graph', 'table'
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  // Get address from navigation state or URL params
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const addressParam = params?.get('address');
    const stateAddress = location?.state?.address;
    
    if (stateAddress) {
      setSelectedAddress(stateAddress);
    } else if (addressParam) {
      setSelectedAddress(addressParam);
    }
  }, [location]);

  const handleAddressSubmit = (e) => {
    e?.preventDefault();
    if (selectedAddress?.trim()) {
      setIsAnalyzing(true);
      // Simulate analysis delay
      setTimeout(() => {
        setIsAnalyzing(false);
      }, 2000);
    }
  };

  const handleNodeClick = (node) => {
    setSelectedNode(node);
    setSelectedAddress(node?.address);
  };

  const handleTransactionSelect = (transaction) => {
    setSelectedTransaction(transaction);
  };

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleExportReport = () => {
    // Mock export functionality
    const reportData = {
      address: selectedAddress,
      timestamp: new Date()?.toISOString(),
      transactions: 'mock_transaction_data',
      patterns: 'mock_pattern_analysis'
    };
    
    console.log('Exporting report:', reportData);
    // In real implementation, this would generate and download a PDF/CSV
  };

  const handleNavigateToReports = () => {
    navigate('/forensic-report-generator', {
      state: {
        address: selectedAddress,
        transactionData: selectedTransaction,
        analysisResults: 'mock_analysis_data'
      }
    });
  };

  const handleNavigateToRiskAssessment = () => {
    navigate('/address-risk-assessment', {
      state: {
        address: selectedAddress
      }
    });
  };

  const handleNavigateToFundFlow = () => {
    navigate('/fund-flow-mapping', {
      state: {
        address: selectedAddress,
        transactionData: selectedTransaction
      }
    });
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-6 py-8">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h1 className="text-3xl font-bold text-text-primary mb-2">
                  Transaction Analysis
                </h1>
                <p className="text-text-secondary">
                  Examine blockchain transactions and trace fund movements through multiple wallet addresses
                </p>
              </div>
              
              <div className="flex items-center space-x-3">
                <Button
                  variant="outline"
                  onClick={() => navigate('/investigation-dashboard')}
                >
                  <Icon name="ArrowLeft" size={16} className="mr-2" />
                  Back to Dashboard
                </Button>
                <Button
                  variant="default"
                  onClick={handleNavigateToReports}
                  disabled={!selectedAddress}
                >
                  <Icon name="FileText" size={16} className="mr-2" />
                  Generate Report
                </Button>
              </div>
            </div>

            {/* Address Input */}
            <div className="bg-surface border border-border rounded-lg p-6 shadow-forensic-card">
              <form onSubmit={handleAddressSubmit} className="flex items-end space-x-4">
                <div className="flex-1">
                  <Input
                    label="Cryptocurrency Address"
                    type="text"
                    placeholder="Enter Bitcoin, Ethereum, or other cryptocurrency address..."
                    value={selectedAddress}
                    onChange={(e) => setSelectedAddress(e?.target?.value)}
                    description="Paste the wallet address you want to analyze for transaction patterns"
                  />
                </div>
                <Button
                  type="submit"
                  variant="default"
                  loading={isAnalyzing}
                  disabled={!selectedAddress?.trim()}
                >
                  <Icon name="Search" size={16} className="mr-2" />
                  Analyze
                </Button>
              </form>
              
              {selectedAddress && (
                <div className="mt-4 flex items-center space-x-4">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleNavigateToRiskAssessment}
                  >
                    <Icon name="Shield" size={16} className="mr-2" />
                    Risk Assessment
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleNavigateToFundFlow}
                  >
                    <Icon name="GitBranch" size={16} className="mr-2" />
                    Fund Flow Mapping
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleExportReport}
                  >
                    <Icon name="Download" size={16} className="mr-2" />
                    Export Data
                  </Button>
                </div>
              )}
            </div>
          </div>

          {selectedAddress && !isAnalyzing && (
            <>
              {/* View Controls */}
              <div className="mb-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-1 bg-muted p-1 rounded-lg">
                    <button
                      onClick={() => setActiveView('split')}
                      className={`
                        flex items-center space-x-2 px-3 py-2 text-sm font-medium rounded-md transition-forensic
                        ${activeView === 'split' ?'bg-surface text-text-primary shadow-forensic-card' :'text-text-secondary hover:text-text-primary'
                        }
                      `}
                    >
                      <Icon name="Layout" size={16} />
                      <span>Split View</span>
                    </button>
                    <button
                      onClick={() => setActiveView('graph')}
                      className={`
                        flex items-center space-x-2 px-3 py-2 text-sm font-medium rounded-md transition-forensic
                        ${activeView === 'graph' ?'bg-surface text-text-primary shadow-forensic-card' :'text-text-secondary hover:text-text-primary'
                        }
                      `}
                    >
                      <Icon name="GitBranch" size={16} />
                      <span>Graph Only</span>
                    </button>
                    <button
                      onClick={() => setActiveView('table')}
                      className={`
                        flex items-center space-x-2 px-3 py-2 text-sm font-medium rounded-md transition-forensic
                        ${activeView === 'table' ?'bg-surface text-text-primary shadow-forensic-card' :'text-text-secondary hover:text-text-primary'
                        }
                      `}
                    >
                      <Icon name="List" size={16} />
                      <span>Table Only</span>
                    </button>
                  </div>
                  
                  <div className="text-sm text-text-secondary">
                    Analyzing: {selectedAddress?.slice(0, 12)}...{selectedAddress?.slice(-8)}
                  </div>
                </div>
              </div>

              {/* Filters */}
              <div className="mb-6">
                <FilterPanel
                  onFiltersChange={handleFiltersChange}
                  isCollapsed={isFilterCollapsed}
                  onToggleCollapse={() => setIsFilterCollapsed(!isFilterCollapsed)}
                />
              </div>

              {/* Main Content Area */}
              <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                {/* Primary Analysis Area */}
                <div className={`
                  ${activeView === 'split' ? 'lg:col-span-3' : 'lg:col-span-4'}
                  space-y-6
                `}>
                  {/* Graph and Table Views */}
                  {activeView === 'split' && (
                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 h-96">
                      <TransactionGraph
                        selectedAddress={selectedAddress}
                        onNodeClick={handleNodeClick}
                        filters={filters}
                      />
                      <TransactionTable
                        onTransactionSelect={handleTransactionSelect}
                        filters={filters}
                      />
                    </div>
                  )}
                  
                  {activeView === 'graph' && (
                    <div className="h-96">
                      <TransactionGraph
                        selectedAddress={selectedAddress}
                        onNodeClick={handleNodeClick}
                        filters={filters}
                      />
                    </div>
                  )}
                  
                  {activeView === 'table' && (
                    <div className="h-96">
                      <TransactionTable
                        onTransactionSelect={handleTransactionSelect}
                        filters={filters}
                      />
                    </div>
                  )}
                </div>

                {/* Pattern Analysis Sidebar */}
                {activeView === 'split' && (
                  <div className="lg:col-span-1">
                    <div className="h-96">
                      <PatternAnalysis
                        selectedTransaction={selectedTransaction}
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Full Width Pattern Analysis for non-split views */}
              {activeView !== 'split' && (
                <div className="mt-6">
                  <div className="h-96">
                    <PatternAnalysis
                      selectedTransaction={selectedTransaction}
                    />
                  </div>
                </div>
              )}

              {/* Quick Actions */}
              <div className="mt-8 bg-surface border border-border rounded-lg p-6 shadow-forensic-card">
                <h3 className="text-lg font-semibold text-text-primary mb-4">Quick Actions</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <Button
                    variant="outline"
                    fullWidth
                    onClick={handleNavigateToRiskAssessment}
                  >
                    <Icon name="Shield" size={16} className="mr-2" />
                    Risk Assessment
                  </Button>
                  <Button
                    variant="outline"
                    fullWidth
                    onClick={handleNavigateToFundFlow}
                  >
                    <Icon name="GitBranch" size={16} className="mr-2" />
                    Fund Flow Mapping
                  </Button>
                  <Button
                    variant="outline"
                    fullWidth
                    onClick={handleNavigateToReports}
                  >
                    <Icon name="FileText" size={16} className="mr-2" />
                    Generate Report
                  </Button>
                  <Button
                    variant="outline"
                    fullWidth
                    onClick={handleExportReport}
                  >
                    <Icon name="Download" size={16} className="mr-2" />
                    Export Analysis
                  </Button>
                </div>
              </div>
            </>
          )}

          {/* Loading State */}
          {isAnalyzing && (
            <div className="flex items-center justify-center py-16">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                <h3 className="text-lg font-semibold text-text-primary mb-2">
                  Analyzing Blockchain Transactions
                </h3>
                <p className="text-text-secondary">
                  Tracing fund movements and identifying transaction patterns...
                </p>
              </div>
            </div>
          )}

          {/* Empty State */}
          {!selectedAddress && !isAnalyzing && (
            <div className="text-center py-16">
              <div className="flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full mx-auto mb-4">
                <Icon name="Search" size={32} color="var(--color-primary)" />
              </div>
              <h3 className="text-lg font-semibold text-text-primary mb-2">
                Start Transaction Analysis
              </h3>
              <p className="text-text-secondary mb-6 max-w-md mx-auto">
                Enter a cryptocurrency address above to begin analyzing blockchain transactions and tracing fund movements.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-2xl mx-auto">
                <div className="text-center p-4 bg-surface border border-border rounded-lg">
                  <Icon name="GitBranch" size={24} color="var(--color-primary)" className="mx-auto mb-2" />
                  <h4 className="font-semibold text-text-primary mb-1">Transaction Graph</h4>
                  <p className="text-sm text-text-secondary">Visualize fund flows between addresses</p>
                </div>
                <div className="text-center p-4 bg-surface border border-border rounded-lg">
                  <Icon name="Brain" size={24} color="var(--color-primary)" className="mx-auto mb-2" />
                  <h4 className="font-semibold text-text-primary mb-1">Pattern Detection</h4>
                  <p className="text-sm text-text-secondary">AI-powered suspicious activity identification</p>
                </div>
                <div className="text-center p-4 bg-surface border border-border rounded-lg">
                  <Icon name="FileText" size={24} color="var(--color-primary)" className="mx-auto mb-2" />
                  <h4 className="font-semibold text-text-primary mb-1">Forensic Reports</h4>
                  <p className="text-sm text-text-secondary">Generate comprehensive analysis reports</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default TransactionAnalysis;